import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";

import { fetchMe } from "../../Redux/Thunks/AuthThunks";
import { getMfaSetupAPI, enableMfaAPI } from "../../Services/AuthServices";
import { ROUTES } from "../../constants";
import { resolvePostLoginNavigatePath } from "../../utils/authReturnUrl";
import logoImage from "../../assets/images/logoNew.jpg";
import UseTitle from "../../Utility/UseTitle/UseTitle";

const COOKIE_KEY = "mfaSetupToken";
const OTP_LENGTH = 6;

function userIdFromJwt(token) {
  if (!token || typeof token !== "string" || !token.includes(".")) return null;
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const payload = JSON.parse(atob(padded));
    const id = payload?.userId ?? payload?.user_id ?? payload?.sub ?? payload?.id;
    return id != null ? String(id) : null;
  } catch {
    return null;
  }
}

function userIdFromMfaSetupResponse(res, setupToken) {
  const body = res?.data;
  const data = body?.data != null && typeof body.data === "object" ? body.data : body;
  if (data && typeof data === "object") {
    const direct = data.userId ?? data.user_id ?? data.id;
    if (direct != null) return String(direct);
    if (typeof data.user === "object") {
      const uid = data.user.id ?? data.user.userId;
      if (uid != null) return String(uid);
    }
    const fromToken = userIdFromJwt(data.accessToken ?? data.access_token);
    if (fromToken) return fromToken;
  }
  try {
    const stored = sessionStorage.getItem("pm_mfa_setup_user_id");
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  return userIdFromJwt(setupToken);
}

function setMfaLoginCookie(userId) {
  const payload = {
    userId,
    method: "email",
    exp: Date.now() + 5 * 60 * 1000,
  };
  Cookies.set("userId", btoa(JSON.stringify(payload)), {
    expires: 1,
    secure: window.location.protocol === "https:",
    sameSite: "Strict",
  });
}

export default function MfaSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const [setupToken, setSetupToken] = useState(null);
  // step: "choose" | "qr" | "verify"
  const [step, setStep] = useState("choose");
  const [qrData, setQrData] = useState(null); // { secret, qrCode }
  const [code, setCode] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = Cookies.get(COOKIE_KEY);
    if (!token) {
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }
    setSetupToken(token);
  }, [navigate]);

  useEffect(() => {
    if (step === "verify") inputRefs.current[0]?.focus();
  }, [step]);

  const handleSelectAuthApp = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await getMfaSetupAPI(setupToken);
      const data = res.data?.data ?? res.data;
      setQrData(data);
      setStep("qr");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate setup code. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEmail = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await enableMfaAPI({ type: "email" }, setupToken);
      const userId = userIdFromMfaSetupResponse(res, setupToken);
      if (userId) {
        setMfaLoginCookie(userId);
      }
      navigate(ROUTES.MFA_LOGIN, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to enable email MFA. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (val, index) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next = [...code];
    next[index] = digit;
    setCode(next);
    if (digit && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    const next = Array(OTP_LENGTH).fill("");
    digits.split("").forEach((d, i) => (next[i] = d));
    setCode(next);
    const last = Math.min(digits.length - 1, OTP_LENGTH - 1);
    inputRefs.current[last]?.focus();
  };

  const handleVerify = async () => {
    const mfa_code = code.join("");
    if (mfa_code.length < OTP_LENGTH) {
      setError("Please enter all 6 digits.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await enableMfaAPI(
        { type: "auth", secret: qrData.secret, code: mfa_code },
        setupToken
      );
      const tokens = res.data?.data ?? res.data;
      await finishSetup(tokens);
    } catch (err) {
      setError(err.response?.data?.message || "Invalid code. Please try again.");
      setCode(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const finishSetup = async (tokens) => {
    Cookies.remove(COOKIE_KEY);
    localStorage.setItem("authToken", JSON.stringify(tokens));
    let me;
    try {
      me = await dispatch(fetchMe()).unwrap();
    } catch {
      me = undefined;
    }
    navigate(resolvePostLoginNavigatePath(me, location), { replace: true });
  };

  if (!setupToken) return null;

  return (
    <div>
      <UseTitle formattedTitle="MFA Setup" />
      <div className="auth-page-wrapper">
        <div className="auth-page-content pb-0">
          <div className="w-full max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center h-screen">
              <div className="w-full md:w-2/3 lg:w-1/2 xl:w-2/5">
                <div className="bg-surface-primary rounded-md border border-border-primary shadow-sm mt-4">
                  <div className="mt-5">
                    <div className="flex justify-center items-center">
                      <img
                        src={logoImage}
                        alt="Logo"
                        className="h-[72px] w-auto max-w-[280px] object-contain"
                      />
                    </div>
                  </div>
                  <div className="p-4 mt-4">
                    <h2 className="text-center text-lg font-semibold text-text-primary mb-1">
                      Set Up Two-Factor Authentication
                    </h2>
                    <p className="text-center text-sm text-text-secondary mb-6">
                      Your account requires MFA. Choose a verification method.
                    </p>

                    {error && (
                      <div className="mb-4 px-3 py-2 rounded bg-red-50 border border-red-200 text-red-600 text-sm text-center">
                        {error}
                      </div>
                    )}

                    {/* ── Step: choose ─────────────────────────────────── */}
                    {step === "choose" && (
                      <div className="flex flex-col gap-3">
                        <button
                          onClick={handleSelectAuthApp}
                          disabled={loading}
                          className="w-full py-3 px-4 rounded-lg border-2 border-border-primary text-text-primary bg-surface-primary hover:border-button-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-left"
                        >
                          <div className="font-medium">Authenticator App</div>
                          <div className="text-sm text-text-secondary mt-0.5">
                            Use Google Authenticator, Authy, or similar
                          </div>
                        </button>
                        <button
                          onClick={handleSelectEmail}
                          disabled={loading}
                          className="w-full py-3 px-4 rounded-lg border-2 border-border-primary text-text-primary bg-surface-primary hover:border-button-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-left"
                        >
                          <div className="font-medium">Email OTP</div>
                          <div className="text-sm text-text-secondary mt-0.5">
                            Receive a one-time code to your email on each login
                          </div>
                        </button>
                        {loading && (
                          <p className="text-center text-sm text-text-secondary">Setting up…</p>
                        )}
                      </div>
                    )}

                    {/* ── Step: qr (show QR + continue to verify) ───── */}
                    {step === "qr" && qrData && (
                      <div className="flex flex-col items-center gap-4">
                        <p className="text-sm text-text-secondary text-center">
                          Scan this QR code with your authenticator app, then enter the code below.
                        </p>
                        <img
                          src={qrData.qrCode}
                          alt="MFA QR Code"
                          className="w-48 h-48 border border-border-primary rounded"
                        />
                        <div className="w-full bg-gray-50 border border-border-primary rounded p-2 text-center">
                          <p className="text-xs text-text-secondary mb-1">
                            Can&apos;t scan? Enter this code manually:
                          </p>
                          <code className="text-xs font-mono break-all select-all text-text-primary">
                            {qrData.secret}
                          </code>
                        </div>
                        <button
                          onClick={() => setStep("verify")}
                          className="w-full py-2 px-4 rounded-lg bg-button-primary text-white font-medium hover:opacity-90 transition-opacity"
                        >
                          I&apos;ve scanned it — Continue
                        </button>
                      </div>
                    )}

                    {/* ── Step: verify (enter TOTP) ─────────────────── */}
                    {step === "verify" && (
                      <div className="flex flex-col items-center gap-4">
                        <p className="text-sm text-text-secondary text-center">
                          Enter the 6-digit code from your authenticator app to confirm setup.
                        </p>
                        <div className="flex justify-center gap-2" onPaste={handlePaste}>
                          {Array.from({ length: OTP_LENGTH }, (_, i) => (
                            <input
                              key={i}
                              ref={(el) => (inputRefs.current[i] = el)}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={code[i]}
                              onChange={(e) => handleCodeChange(e.target.value, i)}
                              onKeyDown={(e) => handleKeyDown(e, i)}
                              disabled={loading}
                              className="w-11 h-12 text-center text-xl font-semibold border border-border-primary rounded-lg bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                          ))}
                        </div>
                        <div className="flex gap-3 w-full">
                          <button
                            type="button"
                            onClick={() => {
                              setStep("qr");
                              setCode(Array(OTP_LENGTH).fill(""));
                              setError("");
                            }}
                            disabled={loading}
                            className="flex-1 py-2 px-4 rounded-lg border border-border-primary text-text-primary bg-surface-primary hover:bg-gray-50 disabled:opacity-50 transition-colors"
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            onClick={handleVerify}
                            disabled={loading}
                            className="flex-1 py-2 px-4 rounded-lg bg-button-primary text-white font-medium hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center gap-2"
                          >
                            {loading ? (
                              <>
                                <svg
                                  className="animate-spin h-4 w-4"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v8H4z"
                                  />
                                </svg>
                                Verifying…
                              </>
                            ) : (
                              "Confirm Setup"
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
